/* eslint-disable no-case-declarations */
/* eslint-disable no-unused-expressions */
import React, { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { DropdownOption } from "./DropdownButton.tsx";

interface DropdownMenuProps {
  options: DropdownOption[];
  buttonRect: DOMRect;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  focusNextElement?: boolean;
}

const supportsPortal = typeof document !== "undefined" &&
  !!document.createElement &&
  !!document.body &&
  !!document.body.appendChild;

const getScrollPosition = () => {
  const x = window.pageXOffset !== undefined ? window.pageXOffset :
    (document.documentElement || document.body.parentNode || document.body).scrollLeft;
  const y = window.pageYOffset !== undefined ? window.pageYOffset :
    (document.documentElement || document.body.parentNode || document.body).scrollTop;
  return { x, y };
};

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  options,
  buttonRect,
  onClose,
  buttonRef,
  focusNextElement = false
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const scrollPos = getScrollPosition();
  const usePortal = supportsPortal;
  const nextFocusableElementRef = useRef<HTMLElement | null>(null);

  // Find the next focusable element after the button
  useEffect(() => {
    if (focusNextElement && buttonRef.current) {
      const focusableSelectors = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusableElements = Array.from(
        document.querySelectorAll(focusableSelectors)
      ) as HTMLElement[];

      // Find the index of our button in the list of all focusable elements
      const buttonIndex = focusableElements.findIndex(el => el === buttonRef.current);

      // Get the next element after our button (if it exists)
      if (buttonIndex !== -1 && buttonIndex < focusableElements.length - 1) {
        nextFocusableElementRef.current = focusableElements[buttonIndex + 1];
      }
    }
  }, [buttonRef, focusNextElement]);

  // Custom close function that handles focus management
  const handleClose = useCallback(() => {
    onClose();

    // Set focus appropriately after closing
    if (focusNextElement && nextFocusableElementRef.current) {
      setTimeout(() => {
        nextFocusableElementRef.current?.focus();
      }, 0);
    } else if (buttonRef.current) {
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 0);
    }
  }, [buttonRef, focusNextElement, onClose]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [buttonRef, handleClose]);

  // Handle keyboard navigation
  useEffect(() => {
    const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]');

    if (menuItems && menuItems.length > 0 && activeIndex === -1) {
      setActiveIndex(0);
      (menuItems[0] as HTMLElement).focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!menuItems || menuItems.length === 0) return;

      switch (event.key) {
      case "Escape":
        event.preventDefault();
        handleClose();
        break;

      case "ArrowDown":
        event.preventDefault();
        if (activeIndex < menuItems.length - 1) {
          const nextIndex = activeIndex + 1;
          setActiveIndex(nextIndex);
          (menuItems[nextIndex] as HTMLElement).focus();
        } else {
          setActiveIndex(0);
          (menuItems[0] as HTMLElement).focus();
        }
        break;

      case "ArrowUp":
        event.preventDefault();
        if (activeIndex > 0) {
          const prevIndex = activeIndex - 1;
          setActiveIndex(prevIndex);
          (menuItems[prevIndex] as HTMLElement).focus();
        } else {
          const lastIndex = menuItems.length - 1;
          setActiveIndex(lastIndex);
          (menuItems[lastIndex] as HTMLElement).focus();
        }
        break;

      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        (menuItems[0] as HTMLElement).focus();
        break;

      case "End":
        event.preventDefault();
        const lastIndex = menuItems.length - 1;
        setActiveIndex(lastIndex);
        (menuItems[lastIndex] as HTMLElement).focus();
        break;

      case "Tab":
        // For tab navigation, close the dropdown and allow focus to move to the next element
        handleClose();
        break;

      default:
        break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex, buttonRef, handleClose, options.length]);

  const renderDropdownContent = () => (
    <div
      ref={menuRef}
      className="bg-white rounded-md shadow-lg border border-gray-200 py-1 focus:outline-none"
      role="menu"
      aria-orientation="vertical"
      tabIndex={-1}
    >
      {options.map((option, index) => {
        const baseClasses = `block px-4 py-2 text-sm ${
          option.disabled
            ? "text-gray-400 cursor-not-allowed"
            : "text-gray-700 hover:bg-gray-100 hover:text-gray-700"
        } ${option.className || ""} focus:outline-none`;

        if (option.to && !option.disabled) {
          return (
            <Link
              key={`${option.label}-${String(index)}`}
              to={option.to}
              className={`${baseClasses} hover:no-underline`}
              onClick={() => {
                option.onClick?.();
                handleClose();
              }}
              role="menuitem"
              tabIndex={0}
            >
              <div className="flex items-center gap-2">
                {option.icon && option.icon}
                {option.label}
              </div>
            </Link>
          );
        }

        if (option.link && !option.disabled) {
          return (
            <a
              key={`${option.label}-${String(index)}`}
              href={option.link}
              className={`${baseClasses} hover:no-underline`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                option.onClick?.();
                handleClose();
              }}
              role="menuitem"
              tabIndex={0}
            >
              <div className="flex items-center gap-2">
                {option.icon && option.icon}
                {option.label}
              </div>
            </a>
          );
        }

        return (
          <button
            key={`${option.label}-${String(index)}`}
            className={`w-full text-left ${baseClasses}`}
            onClick={() => {
              if (!option.disabled) {
                option.onClick?.();
                handleClose();
              }
            }}
            type="button"
            role="menuitem"
            tabIndex={0}
            disabled={option.disabled}
          >
            <div className="flex items-center gap-2">
              {option.icon && option.icon}
              {option.label}
            </div>
          </button>
        );
      })}
    </div>
  );

  const dropdownStyles = {
    position: "absolute" as const,
    top: buttonRect.bottom + scrollPos.y,
    left: buttonRect.left + scrollPos.x,
    width: "15rem",
    zIndex: 50
  };

  const fallbackStyles = {
    position: "absolute" as const,
    top: "100%",
    left: 0,
    zIndex: 50,
    width: "15rem",
    marginTop: "0.5rem"
  };

  if (usePortal) {
    return createPortal(
      <div style={dropdownStyles} className="custom-dropdown dropdown-animation">
        {renderDropdownContent()}
      </div>,
      document.body
    );
  }

  // Fallback for browsers that don't support Portal
  return (
    <div style={fallbackStyles} className="dropdown-animation">
      {renderDropdownContent()}
    </div>
  );
};

export default DropdownMenu;
